import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CustomDateRangePicker, {
  calculateDateRange,
  normalizePresetKey,
} from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import ExpandSearchBar from '../../../../shared-components/expand-search-bar/ExpandSearchBar';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import reportApi from '../../../../utils/api/reports';
import '../../scss/driver-report.scss';

export interface CurrencyAmount {
  currency: string;
  amount: number;
}

export interface DriverReportItem {
  id: string;
  driverId?: string;
  userCustomId?: string;
  date?: string;
  Date?: string;
  formattedDate?: string;
  totalEnergyDelivered?: number;
  totalPowerConsumedKw?: number;
  totalSessions?: number;
  duration?: number;
  totalDurationSeconds?: number;
  averageEnergyPerSessionKw?: number;
  averageDurationPerSessionSeconds?: number;
  averageTotalRevenue?: number;
  averageRevenuePerSession?: CurrencyAmount[];
  totalRevenue?: number;
  totalSpend?: number;
  spendAmount?: CurrencyAmount[];
  revenueAmount?: CurrencyAmount[];
  timePeriod?: string | null;
  [key: string]: any;
}

export interface DriverReportResponse {
  totalEnergyDelivered?: number;
  totalSessions?: number;
  totalPowerConsumedKw?: number;
  duration?: number;
  totalDurationSeconds?: number;
  averageEnergyPerSessionKw?: number;
  averageDurationPerSessionSeconds?: number;
  averageTotalRevenue?: number;
  averageRevenuePerSession?: CurrencyAmount[];
  totalRevenue?: number;
  totalSpend?: number;
  spendAmount?: CurrencyAmount[];
  revenueAmount?: CurrencyAmount[];
  timePeriod?: string | null;
  Date?: string;
  driverId?: string;
  userCustomId?: string;
  startDate?: string | null;
  endDate?: string | null;
  reports?: DriverReportItem[];
  dailyReports?: DriverReportItem[];
  results?: DriverReportItem[];
  data?: DriverReportItem[];
}

export interface DriverReportTableProperties {
  reportType?: 'energy' | 'revenue';
  leftTopContent?: JSX.Element;
  preferenceFilter?: any;
}

/**
 * Driver Report Table Component for Mobility Energy and Revenue reports.
 * Displays energy-related details for energy report and revenue-related details for revenue report with V2 header and default date range.
 * @param {DriverReportTableProperties} properties - Component properties
 * @returns {React.ReactElement} The rendered Driver Report Table
 */
export default function DriverReportTable(
  properties: Readonly<DriverReportTableProperties>,
): React.ReactElement {
  const { reportType = 'energy', leftTopContent } = properties;
  const { t } = useTranslation(['report', 'endConsumer']);

  const [loading, setLoading] = useState<boolean>(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [tableData, setTableData] = useState<DriverReportItem[]>([]);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('last14Days'), 'future');
    return {
      start: defaultRange?.startDate || '',
      end: defaultRange?.endDate || '',
    };
  });
  const previousDateRange = useRef<{ start: string; end: string }>({
    start: dateRange.start,
    end: dateRange.end,
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = (): void => {
    if (previousDateRange.current.start === '' && previousDateRange.current.end === '') {
      return;
    }
    previousDateRange.current = { start: '', end: '' };
    setDateRange({ start: '', end: '' });
    setPage(1);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  /**
   * Fetch Driver Reports from API
   * @param {string} [startDate] - Start date filter
   * @param {string} [endDate] - End date filter
   * @param {string} [search] - Driver search filter
   * @returns {Promise<void>} Promise resolving when fetch finishes
   */
  const fetchDriverReports = useCallback(
    async (
      startDate = dateRange.start,
      endDate = dateRange.end,
      search = debouncedSearch,
    ): Promise<void> => {
      try {
        setLoading(true);
        const query: Record<string, any> = {};

        if (startDate) {
          query.startDate = startDate;
        }
        if (endDate) {
          query.endDate = endDate;
        }

        const trimmed = search.trim();
        if (trimmed) {
          query.search = trimmed;
        }

        const apiCall =
          reportType === 'revenue'
            ? reportApi.fetchDriverRevenueReports(query)
            : reportApi.fetchDriverEnergyReports(query);

        const response = await apiCall;
        const result: DriverReportResponse = response?.data || {};

        const rows: DriverReportItem[] =
          result.reports || result.dailyReports || result.results || result.data || [];

        if (rows.length > 0) {
          setTableData(
            rows.map((row, index) => ({
              ...row,
              id:
                row.id ||
                row._id ||
                `${row.driverId || row.userCustomId || 'driver'}-${row.date || row.Date || index}`,
              driverId: row.driverId || row.userCustomId || trimmed || undefined,
            })),
          );
        } else if (
          result.totalSessions !== undefined ||
          result.totalEnergyDelivered !== undefined ||
          result.totalRevenue !== undefined ||
          result.totalPowerConsumedKw !== undefined
        ) {
          // Fallback to top-level object if single aggregate object returned
          setTableData([
            {
              ...result,
              id: result.Date || 'single-row',
              date: result.Date,
              driverId: result.driverId || result.userCustomId || trimmed || undefined,
            },
          ]);
        } else {
          setTableData([]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch driver reports:', error);
        setLoading(false);
      }
    },
    [reportType, dateRange.start, dateRange.end, debouncedSearch],
  );

  useEffect(() => {
    fetchDriverReports();
  }, [fetchDriverReports]);

  /**
   * Handle Date Range Change
   * @param {string} start - Start date string
   * @param {string} end - End date string
   */
  const handleDateRangeChange = (start: string, end: string): void => {
    if (previousDateRange.current.start === start && previousDateRange.current.end === end) {
      return;
    }
    previousDateRange.current = { start, end };
    setDateRange({ start, end });
    setPage(1);
  };

  /**
   * Handle Search input change
   * @param {string} value - New search query
   */
  const handleSearchChange = (value: string): void => {
    setSearchQuery(value);
    setPage(1);
  };

  /**
   * Render Currency Amounts list
   * @param {CurrencyAmount[]} [amounts] - Array of currency amounts
   * @param {number} [fallbackTotal] - Fallback total if list is empty
   * @returns {React.ReactElement} Formatted currency display
   */
  const renderCurrencyBreakdown = (
    amounts?: CurrencyAmount[],
    fallbackTotal?: number,
  ): React.ReactElement => {
    if (Array.isArray(amounts) && amounts.length > 0) {
      return (
        <div className="currency-badge-list">
          {amounts.map((item, index) => (
            <span key={`${item.currency}-${index}`} className="currency-badge">
              {ToFixedValue(item.amount)} {item.currency}
            </span>
          ))}
        </div>
      );
    }
    return <span className="main-val">{ToFixedValue(fallbackTotal ?? 0)}</span>;
  };

  const filteredData = tableData.filter((item) => {
    if (!searchQuery.trim()) {
      return true;
    }
    const q = searchQuery.toLowerCase().trim();
    const id = (
      item.driverId ||
      item.userCustomId ||
      item.userId ||
      item.driverCustomId ||
      ''
    ).toLowerCase();
    const dateString = (
      item.date ||
      item.Date ||
      item.formattedDate ||
      item.timePeriod ||
      ''
    ).toLowerCase();
    return id.includes(q) || dateString.includes(q);
  });

  const paginatedData = filteredData.slice((page - 1) * limit, page * limit);

  // Common columns
  const baseColumns = [
    {
      label: t('sNo', { defaultValue: 'S.No' }),
      accessorKey: 's_no' as keyof DriverReportItem,
      /**
       * Serial number column renderer
       * @param {DriverReportItem} _row - Current row
       * @param {number} index - Row index
       * @returns {React.ReactElement} Formatted serial number
       */
      customData: (_row: DriverReportItem, index: number) => (
        <div className="driver-table-cell">{(page - 1) * limit + index + 1}</div>
      ),
      columnWidth: 60,
    },
    {
      label: t('driverId', { defaultValue: 'Driver ID' }),
      accessorKey: 'driverId' as keyof DriverReportItem,
      /**
       * Driver ID column renderer
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Formatted driver ID cell
       */
      customData: (row: DriverReportItem) => {
        const idToDisplay =
          row.driverId ||
          row.userCustomId ||
          row.userId ||
          row.driverCustomId ||
          searchQuery.trim() ||
          '-';
        return (
          <div className="driver-table-cell">
            <span className="main-val">{idToDisplay}</span>
          </div>
        );
      },
      columnWidth: 150,
    },
    {
      label: t('date', { defaultValue: 'Date' }),
      accessorKey: 'date' as keyof DriverReportItem,
      /**
       * Date column renderer
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Formatted date cell
       */
      customData: (row: DriverReportItem) => {
        const rawDate = row.date || row.Date || row.formattedDate;
        return (
          <div className="driver-table-cell column-vertical">
            <span className="main-val">
              {rawDate ? ConvertUTCtoDate(rawDate) || rawDate : row.timePeriod || '-'}
            </span>
            {row.timePeriod && <span className="sub-val">{row.timePeriod}</span>}
          </div>
        );
      },
    },
    {
      label: t('totalSessions', { defaultValue: 'Total Sessions' }),
      accessorKey: 'totalSessions' as keyof DriverReportItem,
      /**
       * Total Sessions renderer
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Total sessions cell
       */
      customData: (row: DriverReportItem) => (
        <div className="driver-table-cell">
          <span className="main-val">{row.totalSessions ?? 0}</span>
        </div>
      ),
      columnWidth: 120,
    },
  ];

  // Energy-specific columns
  const energyColumns = [
    {
      label: `${t('totalEnergy', { defaultValue: 'Total Energy' })} (kWh)`,
      accessorKey: 'totalEnergyDelivered' as keyof DriverReportItem,
      /**
       * Total Energy renderer
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Total energy cell
       */
      customData: (row: DriverReportItem) => {
        const energyValue = row.totalEnergyDelivered ?? row.totalPowerConsumedKw ?? 0;
        return (
          <div className="driver-table-cell">
            <span className="main-val">{energyValue}</span>
          </div>
        );
      },
      columnWidth: 160,
    },
    {
      label: t('totalDuration', { defaultValue: 'Total Duration' }),
      accessorKey: 'totalDurationSeconds' as keyof DriverReportItem,
      /**
       * Total Duration renderer
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Duration cell
       */
      customData: (row: DriverReportItem) => {
        const durationSec = row.totalDurationSeconds ?? row.duration ?? 0;
        return (
          <div className="driver-table-cell">
            <span className="main-val">{FormatDuration(durationSec, { formatBy: 'sec' })}</span>
          </div>
        );
      },
      columnWidth: 140,
    },
    {
      label: `${t('avgEnergyPerSession', { defaultValue: 'Avg Energy / Session' })} (kWh)`,
      accessorKey: 'averageEnergyPerSessionKw' as keyof DriverReportItem,
      /**
       * Average Energy per session renderer
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Average energy cell
       */
      customData: (row: DriverReportItem) => (
        <div className="driver-table-cell">
          <span className="main-val">{row.averageEnergyPerSessionKw ?? 0}</span>
        </div>
      ),
      columnWidth: 180,
    },
    {
      label: t('avgDurationPerSession', { defaultValue: 'Avg Duration / Session' }),
      accessorKey: 'averageDurationPerSessionSeconds' as keyof DriverReportItem,
      /**
       * Average Duration per session renderer
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Average duration cell
       */
      customData: (row: DriverReportItem) => (
        <div className="driver-table-cell">
          <span className="main-val">
            {FormatDuration(row.averageDurationPerSessionSeconds, { formatBy: 'sec' })}
          </span>
        </div>
      ),
      columnWidth: 160,
    },
  ];

  // Revenue-specific columns
  const revenueColumns = [
    {
      label: t('totalRevenue', { defaultValue: 'Total Revenue / Spend' }),
      accessorKey: 'totalRevenue' as keyof DriverReportItem,
      /**
       * Total Revenue / Spend renderer with currency breakdown
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Total revenue cell
       */
      customData: (row: DriverReportItem) => {
        const spendList = row.spendAmount || row.revenueAmount;
        const totalAmount = row.totalRevenue ?? row.totalSpend ?? 0;
        return (
          <div className="driver-table-cell column-vertical">
            <span className="main-val">{ToFixedValue(totalAmount)}</span>
            {renderCurrencyBreakdown(spendList)}
          </div>
        );
      },
      columnWidth: 180,
    },
    {
      label: t('totalDuration', { defaultValue: 'Total Duration' }),
      accessorKey: 'totalDurationSeconds' as keyof DriverReportItem,
      /**
       * Total Duration renderer
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Duration cell
       */
      customData: (row: DriverReportItem) => {
        const durationSec = row.totalDurationSeconds ?? row.duration ?? 0;
        return (
          <div className="driver-table-cell">
            <span className="main-val">{FormatDuration(durationSec, { formatBy: 'sec' })}</span>
          </div>
        );
      },
      columnWidth: 140,
    },
    {
      label: t('avgRevenuePerSession', { defaultValue: 'Avg Revenue / Session' }),
      accessorKey: 'averageTotalRevenue' as keyof DriverReportItem,
      /**
       * Average Revenue per session renderer
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Average revenue cell
       */
      customData: (row: DriverReportItem) => {
        const avgList = row.averageRevenuePerSession;
        const avgAmount = row.averageTotalRevenue ?? 0;
        return (
          <div className="driver-table-cell column-vertical">
            <span className="main-val">{ToFixedValue(avgAmount)}</span>
            {renderCurrencyBreakdown(avgList)}
          </div>
        );
      },
      columnWidth: 180,
    },
    {
      label: t('avgDurationPerSession', { defaultValue: 'Avg Duration / Session' }),
      accessorKey: 'averageDurationPerSessionSeconds' as keyof DriverReportItem,
      /**
       * Average Duration per session renderer
       * @param {DriverReportItem} row - Current row
       * @returns {React.ReactElement} Average duration cell
       */
      customData: (row: DriverReportItem) => (
        <div className="driver-table-cell">
          <span className="main-val">
            {FormatDuration(row.averageDurationPerSessionSeconds, { formatBy: 'sec' })}
          </span>
        </div>
      ),
      columnWidth: 160,
    },
  ];

  const columns =
    reportType === 'energy'
      ? [...baseColumns, ...energyColumns]
      : [...baseColumns, ...revenueColumns];

  /**
   * Export driver reports to CSV or PDF
   * @param {string} format - The export format ('csv' | 'pdf')
   */
  const handleExport = (format: string): void => {
    setExportLoader(true);
    try {
      let headers: string[];
      let rows: (string | number)[][];

      if (reportType === 'energy') {
        headers = [
          t('sNo', { defaultValue: 'S.No' }),
          t('driverId', { defaultValue: 'Driver ID' }),
          t('date', { defaultValue: 'Date' }),
          t('totalSessions', { defaultValue: 'Total Sessions' }),
          `${t('totalEnergy', { defaultValue: 'Total Energy' })} (kWh)`,
          t('totalDuration', { defaultValue: 'Total Duration' }),
          `${t('avgEnergyPerSession', { defaultValue: 'Avg Energy / Session' })} (kWh)`,
          t('avgDurationPerSession', { defaultValue: 'Avg Duration / Session' }),
        ];

        rows = filteredData.map((item, index) => {
          const driverIdString =
            item.driverId ||
            item.userCustomId ||
            item.userId ||
            item.driverCustomId ||
            searchQuery.trim() ||
            '-';
          const dateString = item.date || item.Date || item.formattedDate || item.timePeriod || '-';
          const energyString = `${item.totalEnergyDelivered ?? item.totalPowerConsumedKw ?? 0}`;
          const durationString = FormatDuration(item.totalDurationSeconds ?? item.duration ?? 0);
          const avgEnergyString = `${item.averageEnergyPerSessionKw ?? 0}`;
          const avgDurationString = FormatDuration(item.averageDurationPerSessionSeconds ?? 0);

          return [
            index + 1,
            driverIdString,
            dateString,
            item.totalSessions ?? 0,
            energyString,
            durationString,
            avgEnergyString,
            avgDurationString,
          ];
        });
      } else {
        headers = [
          t('sNo', { defaultValue: 'S.No' }),
          t('driverId', { defaultValue: 'Driver ID' }),
          t('date', { defaultValue: 'Date' }),
          t('totalSessions', { defaultValue: 'Total Sessions' }),
          t('totalRevenue', { defaultValue: 'Total Revenue' }),
          t('totalDuration', { defaultValue: 'Total Duration' }),
          t('avgRevenuePerSession', { defaultValue: 'Avg Revenue / Session' }),
          t('avgDurationPerSession', { defaultValue: 'Avg Duration / Session' }),
        ];

        rows = filteredData.map((item, index) => {
          const driverIdString =
            item.driverId ||
            item.userCustomId ||
            item.userId ||
            item.driverCustomId ||
            searchQuery.trim() ||
            '-';
          const dateString = item.date || item.Date || item.formattedDate || item.timePeriod || '-';
          const revenueString = `${item.totalRevenue ?? item.totalSpend ?? 0}`;
          const durationString = FormatDuration(item.totalDurationSeconds ?? item.duration ?? 0);
          const avgRevenueString = `${item.averageTotalRevenue ?? 0}`;
          const avgDurationString = FormatDuration(item.averageDurationPerSessionSeconds ?? 0);

          return [
            index + 1,
            driverIdString,
            dateString,
            item.totalSessions ?? 0,
            revenueString,
            durationString,
            avgRevenueString,
            avgDurationString,
          ];
        });
      }

      const fileName = `driver_${reportType}_report_${dayjs().format('YYYYMMDD_HHmmss')}`;
      if (format === 'csv') {
        exportCSV(headers, rows, `${fileName}.csv`);
      } else {
        exportPDF(headers, rows, `${fileName}.pdf`);
      }
      setExportLoader(false);
    } catch (error) {
      console.error('Export failed:', error);
      setExportLoader(false);
    }
  };

  /**
   * Render search filter for the left side of the V2 header
   * @returns {React.ReactElement} The left filter content element
   */
  const renderLeftFilterContent = (): React.ReactElement => (
    <div className="driver-report-left-header">
      <div className="driver-search-wrap">
        <ExpandSearchBar
          placeholder={t('searchDriverId', { defaultValue: 'Search by Driver ID' })}
          value={searchQuery}
          onChange={(value) => handleSearchChange(String(value))}
        />
      </div>
    </div>
  );

  /**
   * Render date filter for the right side of the V2 header
   * @returns {React.ReactElement} The right filter content element
   */
  const renderRightFilterContent = (): React.ReactElement => (
    <div className="driver-report-left-header">
      <div className="driver-date-wrap">
        <CustomDateRangePicker
          startDate={dateRange.start}
          endDate={dateRange.end}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          onChange={handleDateRangeChange}
          onClear={clearDateRange}
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    </div>
  );

  return (
    <div className="driver-report-container">
      <CustomTable
        disablePagination={false}
        loading={loading}
        limit={limit}
        header={columns as any}
        data={paginatedData}
        page={page}
        totalPages={Math.ceil(filteredData.length / limit) || 1}
        totalResults={filteredData.length}
        pageLimitChange={(apiRequest) => {
          if (apiRequest.page) setPage(apiRequest.page);
          if (apiRequest.limit) setLimit(apiRequest.limit);
        }}
        version="v2"
        isExportRequired
        exportDataPdfOrCSV={handleExport}
        exportData={filteredData}
        exportLoader={exportLoader}
        disableHeader={false}
        disableSearch
        leftTopContent={leftTopContent || renderLeftFilterContent()}
        rightTopContent={renderRightFilterContent()}
        refershCurrentPages={() => fetchDriverReports()}
        sizeChangeButtonRequired
        onClearFilterChange={clearDateRange}
      />
    </div>
  );
}
